import { toast } from '@econsult/econsult-ui/dist/components/Toast';
import { ErrorToast, SuccessToast } from 'components/Toast';
import { STRINGS } from 'config';
import _find from 'lodash/find';
import _map from 'lodash/map';
import React, { Dispatch } from 'react';
import { Template, TemplateBody } from 'store/reducer/templates';
import request from 'superagent';

const { EMIS } = STRINGS.INTEROP.NAME;

interface ApiError {
  status?: number;
  response?: {
    body?: {
      errors?: Array<{ error: string; property: string }>;
    };
  };
}

const getUpdateTemplateErrors = (error: ApiError): [string, { isDuplicateName?: boolean }] => {
  if (error.status === 404) {
    return ['Template has been deleted', {}];
  }
  const isDuplicateName = _find(
    error.response?.body?.errors,
    errorType => errorType.error === 'duplicate' && errorType.property === 'name',
  );
  if (isDuplicateName) {
    return ['Template name already exists', { isDuplicateName: true }];
  }
  return ['Could not update template', {}];
};

const roles = {
  GLOBAL: 'GLOBAL',
  SHARED: 'PRACTICE',
  PERSONAL: 'USER',
} as const;

export function mapTemplate(template: {
  id: string;
  name: string;
  practiceRole: string;
  sectionHeadings?: string[];
  visibility: string;
  transcriptionType?: 'Consultation' | 'Dictation';
}) {
  return {
    id: template.id,
    name: template.name,
    practiceRole: template.practiceRole,
    content: (template.sectionHeadings || []).join('\n'),
    type: template.visibility === 'USER' ? 'PERSONAL' : 'SHARED',
    transcriptionType: template.transcriptionType || 'Consultation',
  };
}

export function createSectionHeadings(input: string) {
  return input.split(/\r?\n/);
}

export type VisibilityType = keyof typeof roles;

export function mapPractice(role: string) {
  if (role in roles) {
    return roles[role as keyof typeof roles];
  }
  return null;
}
const {
  SUMMARISER: { HOST },
  REDUCERS: {
    TEMPLATES_REDUCER: {
      GET_TEMPLATES_REQUEST,
      GET_TEMPLATES_ERROR,
      GET_TEMPLATES_SUCCESS,
      DELETE_TEMPLATE_REQUEST,
      DELETE_TEMPLATE_ERROR,
      DELETE_TEMPLATE_SUCCESS,
    },
  },
} = STRINGS;

export const selectTemplateInTemplateManager = (
  template: any, // eslint-disable-line @typescript-eslint/no-explicit-any
) => ({ type: STRINGS.REDUCERS.TEMPLATES_REDUCER.SELECT_TEMPLATE_IN_TEMPLATE_MANAGER, payload: template });
export const clearTemplateInTemplateManager = () => ({
  type: STRINGS.REDUCERS.TEMPLATES_REDUCER.CLEAR_TEMPLATE_IN_TEMPLATE_MANAGER,
});
export const updateTemplateName = (name: string) => ({
  type: STRINGS.REDUCERS.TEMPLATES_REDUCER.UPDATE_TEMPLATE_NAME,
  payload: name,
});
export const updateTemplatePracticeRole = (role: string) => ({
  type: STRINGS.REDUCERS.TEMPLATES_REDUCER.UPDATE_TEMPLATE_PRACTICE_ROLE,
  payload: role,
});
export const updateTemplateTranscriptionType = (transcriptionType: 'Consultation' | 'Dictation') => ({
  type: STRINGS.REDUCERS.TEMPLATES_REDUCER.UPDATE_TEMPLATE_TRANSCRIPTION_TYPE,
  payload: transcriptionType,
});
export const updateTemplateContent = (content: string) => ({
  type: STRINGS.REDUCERS.TEMPLATES_REDUCER.UPDATE_TEMPLATE_CONTENT,
  payload: content,
});
export const updateTemplateType = (templateType: string) => ({
  type: STRINGS.REDUCERS.TEMPLATES_REDUCER.UPDATE_TEMPLATE_TYPE,
  payload: templateType,
});
export const setShowTemplateValidation = (isInvalid: boolean) => ({
  type: STRINGS.REDUCERS.TEMPLATES_REDUCER.SET_SHOW_TEMPLATE_VALIDATION,
  payload: isInvalid,
});
export const selectTemplate = (template: Template) => ({
  type: STRINGS.REDUCERS.TEMPLATES_REDUCER.SELECT_TEMPLATE,
  payload: template,
});

export const getTemplates = (
  dispatch: Dispatch<unknown>,
  selectedPracticeId: string,
  clinicalSystemName: string | null,
  featureFlags?: any, // eslint-disable-line @typescript-eslint/no-explicit-any
) => {
  dispatch({ type: GET_TEMPLATES_REQUEST });
  return request
    .get(`${HOST}/templates?practiceId=${selectedPracticeId}`)
    .set('Content-Type', 'application/json')
    .withCredentials()
    .then(({ body }) => {
      const mappedTemplates = _map(body, mapTemplate).filter(template => {
        // Exclude EMIS_WEB_TEMPLATE if:
        // 1. The clinical system is not EMIS, OR
        // 2. The clinical system is EMIS AND the feature flag explicitly disables it
        if (
          template.name === STRINGS.TEMPLATE.EMIS_WEB_TEMPLATE &&
          (clinicalSystemName !== EMIS ||
            (clinicalSystemName === EMIS && featureFlags?.SUMMARISER_SHOW_EMIS_WEB_TEMPLATE === false))
        ) {
          return false;
        }
        return true;
      });

      return dispatch({ type: GET_TEMPLATES_SUCCESS, payload: mappedTemplates });
    })
    .catch(error => {
      dispatch({ type: GET_TEMPLATES_ERROR, payload: error });
      toast.error(
        React.createElement(ErrorToast, {
          message: 'Could not retrieve templates',
        }),
        {
          autoClose: 12000,
          pauseOnHover: true,
          hideProgressBar: false,
          closeOnClick: true,
          icon: false,
        },
      );
    });
};

export const addNewTemplate = (template: TemplateBody, dispatch: Dispatch<unknown>, selectedPracticeId: string) => {
  dispatch({ type: STRINGS.REDUCERS.TEMPLATES_REDUCER.ADD_NEW_TEMPLATE_REQUEST });
  return request
    .post(`${HOST}/templates?practiceId=${selectedPracticeId}`)
    .set('Content-Type', 'application/json')
    .send(template)
    .withCredentials()
    .then(response => {
      const templateWithDbID = { ...template, id: response.body.id };
      dispatch({ type: STRINGS.REDUCERS.TEMPLATES_REDUCER.ADD_NEW_TEMPLATE_SUCCESS, payload: templateWithDbID });
      toast.success(
        React.createElement(SuccessToast, {
          message: 'New template added',
        }),
        {
          autoClose: 12000,
          pauseOnHover: true,
          hideProgressBar: false,
          closeOnClick: true,
          icon: false,
        },
      );
    })
    .catch(error => {
      const isDuplicateName = error.response?.body?.error?.includes('A template with that name already exists');
      const toastMessage = error.response?.body?.error || 'Could not add new template';
      dispatch({
        type: STRINGS.REDUCERS.TEMPLATES_REDUCER.ADD_NEW_TEMPLATE_ERROR,
        payload: { id: 'newTemplate', apiErrors: { isDuplicateName: isDuplicateName } },
      });
      toast.error(
        React.createElement(ErrorToast, {
          message: toastMessage,
        }),
        {
          autoClose: 12000,
          pauseOnHover: true,
          hideProgressBar: false,
          closeOnClick: true,
          icon: false,
        },
      );
    });
};

export const updateTemplate = (template: TemplateBody, dispatch: Dispatch<unknown>, selectedPracticeId: string) => {
  dispatch({ type: STRINGS.REDUCERS.TEMPLATES_REDUCER.UPDATE_TEMPLATE_REQUEST });
  return request
    .put(`${HOST}/templates/${template.id}?practiceId=${selectedPracticeId}`)
    .set('Content-Type', 'application/json')
    .send(template)
    .withCredentials()
    .then(() => {
      dispatch({ type: STRINGS.REDUCERS.TEMPLATES_REDUCER.UPDATE_TEMPLATE_SUCCESS, payload: template });
      toast.success(
        React.createElement(SuccessToast, {
          message: 'Template updated',
        }),
        {
          autoClose: 12000,
          pauseOnHover: true,
          hideProgressBar: false,
          closeOnClick: true,
          icon: false,
        },
      );
    })
    .catch(error => {
      const [toastErrorMessage, errorsAsBooleans] = getUpdateTemplateErrors(error);
      dispatch({
        type: STRINGS.REDUCERS.TEMPLATES_REDUCER.UPDATE_TEMPLATE_ERROR,
        payload: { id: template.id, apiErrors: errorsAsBooleans },
      });
      toast.error(
        React.createElement(ErrorToast, {
          message: toastErrorMessage || 'Failed to update template',
        }),
        {
          autoClose: 12000,
          pauseOnHover: true,
          hideProgressBar: false,
          closeOnClick: true,
          icon: false,
        },
      );
    });
};

export const deleteTemplateFromTemplateManager = (
  templateId: string,
  dispatch: Dispatch<unknown>,
  selectedPracticeId: string,
) => {
  dispatch({ type: DELETE_TEMPLATE_REQUEST });
  return request
    .delete(`${HOST}/templates/${templateId}?practiceId=${selectedPracticeId}`)
    .set('Content-Type', 'application/json')
    .withCredentials()
    .then(() => {
      dispatch({ type: DELETE_TEMPLATE_SUCCESS, payload: templateId });
      toast.success(React.createElement(SuccessToast, { message: 'Template deleted successfully' }), {
        autoClose: 12000,
        pauseOnHover: true,
        hideProgressBar: false,
        closeOnClick: true,
        icon: false,
      });
      dispatch(clearTemplateInTemplateManager());
    })
    .catch(error => {
      dispatch({ type: DELETE_TEMPLATE_ERROR, payload: error });
      toast.error(
        React.createElement(ErrorToast, {
          message: 'Error deleting template, please try again',
        }),
        {
          autoClose: 12000,
          pauseOnHover: true,
          hideProgressBar: false,
          closeOnClick: true,
          icon: false,
        },
      );
    });
};
